import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Grid,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import WorkRequestHandler from '../components/work-requests/WorkRequestHandler';
import { db } from '../firebase/config';
import { collection, query, where, orderBy, getDocs, doc, getDoc, deleteDoc } from 'firebase/firestore';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

// Constantes pour les statuts et priorités
const STATUS = {
  PENDING: 'pending',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled'
};

const STATUS_LABELS = {
  [STATUS.PENDING]: 'En attente',
  [STATUS.IN_PROGRESS]: 'En cours',
  [STATUS.COMPLETED]: 'Terminé',
  [STATUS.CANCELLED]: 'Annulé'
};

const STATUS_COLORS = {
  [STATUS.PENDING]: 'warning',
  [STATUS.IN_PROGRESS]: 'info',
  [STATUS.COMPLETED]: 'success',
  [STATUS.CANCELLED]: 'error'
};

const PRIORITY = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  URGENT: 'urgent'
};

const PRIORITY_LABELS = {
  [PRIORITY.LOW]: 'Basse',
  [PRIORITY.MEDIUM]: 'Moyenne',
  [PRIORITY.HIGH]: 'Haute',
  [PRIORITY.URGENT]: 'Urgente'
};

const PRIORITY_COLORS = {
  [PRIORITY.LOW]: 'success',
  [PRIORITY.MEDIUM]: 'info',
  [PRIORITY.HIGH]: 'warning',
  [PRIORITY.URGENT]: 'error'
};

export default function WorkRequestList() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentUser, userRole, userService, userSchool } = useAuth();
  const [workRequests, setWorkRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    search: ''
  });
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [requestToDelete, setRequestToDelete] = useState(null);
  const [selectedRequests, setSelectedRequests] = useState([]);
  const [selectMode, setSelectMode] = useState(false);

  useEffect(() => {
    console.log('WorkRequestList - useEffect déclenché', {
      currentUser: currentUser?.uid,
      userRole,
      userService: userService?.id,
      userSchool: userSchool?.id
    });
    fetchWorkRequests();
  }, [currentUser, userRole, userService, userSchool]);

  // Ouvrir automatiquement une demande si requestId est dans l'URL
  useEffect(() => {
    const requestId = searchParams.get('requestId');
    if (requestId && workRequests.length > 0) {
      const request = workRequests.find(r => r.id === requestId);
      if (request) {
        console.log('WorkRequestList: Ouverture automatique de la demande', requestId);
        handleViewRequest(requestId); // Passer l'ID, pas l'objet
        // Nettoyer l'URL après avoir ouvert la demande
        navigate('/demandes', { replace: true });
      }
    }
  }, [searchParams, workRequests]);

  const fetchWorkRequests = async () => {
    try {
      console.log('WorkRequestList - Début fetchWorkRequests');
      setLoading(true);
      setError('');

      let workRequestsQuery = collection(db, 'workRequests');
      let constraints = [];

      // Filtrer selon le rôle et le service/école
      console.log('WorkRequestList - Filtrage par rôle:', { userRole, userServiceId: userService?.id, userSchoolId: userSchool?.id });
      
      if (userRole === 'service') {
        console.log('WorkRequestList - Filtrage pour service normal');
        constraints.push(where('requestingServiceId', '==', userService?.id));
      } else if (userRole === 'technical') {
        console.log('WorkRequestList - Filtrage pour service technique');
        constraints.push(where('assignedServiceId', '==', userService?.id));
      } else if (userRole === 'requester') {
        console.log('WorkRequestList - Filtrage pour demandeur (requester)');
        // Pour le rôle requester, on affiche les demandes créées par l'utilisateur
        if (currentUser) {
          console.log('WorkRequestList - Filtrage par createdBy pour demandeur:', currentUser.uid);
          constraints.push(where('createdBy', '==', currentUser.uid));
        } else {
          console.log('WorkRequestList - Demandeur non authentifié');
        }
      } else if (userSchool) {
        console.log('WorkRequestList - Filtrage pour admin avec école');
        constraints.push(where('schoolId', '==', userSchool.id));
      } else {
        console.log('WorkRequestList - Aucun filtre appliqué - cas par défaut');
      }

      // Ajouter les filtres de statut et priorité s'ils sont définis
      if (filters.status) {
        constraints.push(where('status', '==', filters.status));
      }
      if (filters.priority) {
        constraints.push(where('priority', '==', filters.priority));
      }

      // Ajouter le tri par date de création
      constraints.push(orderBy('createdAt', 'desc'));

      console.log('WorkRequestList - Contraintes de requête:', constraints);
      const q = query(workRequestsQuery, ...constraints);
      const querySnapshot = await getDocs(q);
      
      console.log(`WorkRequestList - Résultat de la requête: ${querySnapshot.docs.length} documents trouvés`);
      
      const requests = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      console.log('WorkRequestList - Données des demandes:', requests);

      // Filtrer par recherche si nécessaire
      const filteredRequests = filters.search
        ? requests.filter(request => {
            const titleMatch = request.title ? request.title.toLowerCase().includes(filters.search.toLowerCase()) : false;
            const descMatch = request.description ? request.description.toLowerCase().includes(filters.search.toLowerCase()) : false;
            return titleMatch || descMatch;
          })
        : requests;
      
      console.log(`WorkRequestList - Après filtrage par recherche: ${filteredRequests.length} demandes`);
      console.log('WorkRequestList - Filtres appliqués:', filters);

      setWorkRequests(filteredRequests);
    } catch (err) {
      console.error('WorkRequestList - Erreur lors de la récupération des demandes:', err);
      console.error('WorkRequestList - Stack trace:', err.stack);
      setError('Une erreur est survenue lors du chargement des demandes: ' + err.message);
    } finally {
      console.log('WorkRequestList - Fin fetchWorkRequests');
      setLoading(false);
    }
  };

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };


  const handleViewRequest = async (requestId) => {
    try {
      setLoading(true);
      const docRef = doc(db, 'workRequests', requestId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        setSelectedRequest({
          id: docSnap.id,
          ...docSnap.data()
        });
      } else {
        setError('Demande non trouvée');
      }
    } catch (err) {
      console.error('Erreur lors de la récupération de la demande:', err);
      setError('Une erreur est survenue lors du chargement de la demande.');
    } finally {
      setLoading(false);
    }
  };

  const handleEditRequest = (requestId) => {
    handleViewRequest(requestId);
  };

  const handleDeleteRequest = (requestId) => {
    setRequestToDelete(requestId);
    setDeleteDialogOpen(true);
  };
  
  const toggleSelectMode = () => {
    setSelectMode(!selectMode);
    if (selectMode) {
      // Si on désactive le mode sélection, on vide la liste des demandes sélectionnées
      setSelectedRequests([]);
    }
  };
  
  const toggleRequestSelection = (requestId) => {
    setSelectedRequests(prev => {
      if (prev.includes(requestId)) {
        return prev.filter(id => id !== requestId);
      } else {
        return [...prev, requestId];
      }
    });
  };
  
  const handleGenerateMultiplePDFs = () => {
    if (selectedRequests.length === 0) {
      setError('Veuillez sélectionner au moins une demande');
      return;
    }
    generatePDF(selectedRequests);
  };

  const generatePDF = async (requestIds) => {
    try {
      setLoading(true);
      
      // Convertir en tableau si c'est une seule ID
      const idsArray = Array.isArray(requestIds) ? requestIds : [requestIds];
      
      // Créer un nouveau document PDF
      const pdf = new jsPDF();
      
      // Ajouter un logo ou en-tête si nécessaire
      pdf.setFillColor(41, 98, 255); // Couleur bleue pour l'en-tête
      pdf.rect(0, 0, pdf.internal.pageSize.width, 15, 'F');
      
      pdf.setTextColor(255, 255, 255); // Texte blanc pour l'en-tête
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(14);
      pdf.text('EduWorks - Demandes de travaux', 10, 10);
      
      // Réinitialiser les styles de texte
      pdf.setTextColor(0, 0, 0);
      pdf.setFont('helvetica', 'normal');
      
      let currentPage = 1;
      let yPosition = 30;
      
      // Traiter chaque demande
      for (let i = 0; i < idsArray.length; i++) {
        const requestId = idsArray[i];
        const docRef = doc(db, 'workRequests', requestId);
        const docSnap = await getDoc(docRef);
        
        if (!docSnap.exists()) {
          console.warn(`Demande ${requestId} non trouvée`);
          continue;
        }
        
        const requestData = {
          id: docSnap.id,
          ...docSnap.data()
        };
        
        // Ajouter une nouvelle page si ce n'est pas la première demande
        if (i > 0) {
          pdf.addPage();
          currentPage++;
          yPosition = 30;
          
          // Ajouter l'en-tête sur la nouvelle page
          pdf.setFillColor(41, 98, 255);
          pdf.rect(0, 0, pdf.internal.pageSize.width, 15, 'F');
          pdf.setTextColor(255, 255, 255);
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(14);
          pdf.text('EduWorks - Demandes de travaux', 10, 10);
          pdf.setTextColor(0, 0, 0);
          pdf.setFont('helvetica', 'normal');
        }
        
        // Titre de la demande
        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.text(`Détails de la demande: ${requestData.title || 'Sans titre'}`, 14, yPosition);
        yPosition += 10;
        
        // Créer un tableau pour les informations principales
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        
        // Définir les colonnes et les données pour le tableau d'informations
        const tableData = [
          ['Date de création', requestData.createdAt ? format(requestData.createdAt.toDate(), 'dd/MM/yyyy HH:mm', { locale: fr }) : 'Non spécifié'],
          ['Statut', STATUS_LABELS[requestData.status] || requestData.status || 'Non spécifié'],
          ['Priorité', PRIORITY_LABELS[requestData.priority] || requestData.priority || 'Non spécifié'],
          ['Service demandeur', requestData.requestingServiceName || requestData.serviceName || 'Non spécifié'],
          ['Service technique', requestData.technicalServiceName || (requestData.handledBy ? requestData.handledBy.name : 'Non spécifié')],
          ['Service concerné', requestData.concernedServiceName || 'Non spécifié'],
          ['Localisation', requestData.location ? (typeof requestData.location === 'string' ? requestData.location : `${requestData.location.building || ''}${requestData.location.floor ? `, Étage ${requestData.location.floor}` : ''}${requestData.location.room ? `, Salle ${requestData.location.room}` : ''}`) : requestData.locationText || 'Non spécifié']
        ];
        
        // Générer le tableau
        pdf.autoTable({
          startY: yPosition,
          head: [['Propriété', 'Valeur']],
          body: tableData,
          theme: 'striped',
          headStyles: { fillColor: [66, 139, 202], textColor: [255, 255, 255] },
          alternateRowStyles: { fillColor: [240, 240, 240] },
          margin: { top: 10 }
        });
        
        yPosition = pdf.lastAutoTable.finalY + 10;
        
        // Ajouter la description
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Description:', 14, yPosition);
        yPosition += 7;
        
        pdf.setFont('helvetica', 'normal');
        const splitDescription = pdf.splitTextToSize(requestData.description || 'Aucune description', 180);
        pdf.text(splitDescription, 14, yPosition);
        yPosition += splitDescription.length * 7 + 10;
        
        // Ajouter le dernier commentaire s'il existe
        if (requestData.lastComment) {
          pdf.setFont('helvetica', 'bold');
          pdf.text('Dernier commentaire:', 14, yPosition);
          yPosition += 7;
          
          pdf.setFont('helvetica', 'normal');
          const splitComment = pdf.splitTextToSize(requestData.lastComment, 180);
          pdf.text(splitComment, 14, yPosition);
          yPosition += splitComment.length * 7 + 10;
        }
        
        // Ajouter un pied de page
        pdf.setFontSize(8);
        pdf.setTextColor(100, 100, 100);
        pdf.text(`Page ${currentPage}/${idsArray.length}`, pdf.internal.pageSize.width / 2, pdf.internal.pageSize.height - 10, { align: 'center' });
        pdf.text(`Généré le ${format(new Date(), 'dd/MM/yyyy HH:mm', { locale: fr })}`, pdf.internal.pageSize.width - 14, pdf.internal.pageSize.height - 10, { align: 'right' });
        pdf.setTextColor(0, 0, 0);
      }
      
      // Enregistrer le PDF
      const filename = idsArray.length === 1 ? `demande-${idsArray[0]}.pdf` : `demandes-travaux-${format(new Date(), 'yyyyMMdd-HHmm')}.pdf`;
      pdf.save(filename);
    } catch (err) {
      console.error('Erreur lors de la génération du PDF:', err);
      setError('Une erreur est survenue lors de la génération du PDF.');
    } finally {
      setLoading(false);
    }
  };

  const confirmDeleteRequest = async () => {
    if (!requestToDelete) return;
    
    try {
      setLoading(true);
      await deleteDoc(doc(db, 'workRequests', requestToDelete));
      setWorkRequests(prev => prev.filter(req => req.id !== requestToDelete));
      setDeleteDialogOpen(false);
      setRequestToDelete(null);
    } catch (err) {
      console.error('Erreur lors de la suppression de la demande:', err);
      setError('Une erreur est survenue lors de la suppression de la demande.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4">Demandes de travaux</Typography>
          <Box>
            {selectMode && selectedRequests.length > 0 && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<PdfIcon />}
                onClick={handleGenerateMultiplePDFs}
                sx={{ mr: 1 }}
              >
                Générer PDF ({selectedRequests.length})
              </Button>
            )}
            <Button
              variant="outlined"
              color={selectMode ? "secondary" : "primary"}
              onClick={toggleSelectMode}
              sx={{ mr: 1 }}
            >
              {selectMode ? "Annuler la sélection" : "Sélection multiple"}
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Rechercher"
              name="search"
              value={filters.search}
              onChange={handleFilterChange}
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Statut</InputLabel>
              <Select
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
                label="Statut"
              >
                <MenuItem value="">Tous</MenuItem>
                {Object.entries(STATUS_LABELS).map(([value, label]) => (
                  <MenuItem key={value} value={value}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Priorité</InputLabel>
              <Select
                name="priority"
                value={filters.priority}
                onChange={handleFilterChange}
                label="Priorité"
              >
                <MenuItem value="">Toutes</MenuItem>
                {Object.entries(PRIORITY_LABELS).map(([value, label]) => (
                  <MenuItem key={value} value={value}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                {selectMode && <TableCell padding="checkbox">Sélection</TableCell>}
                <TableCell>Titre</TableCell>
                <TableCell>Date de création</TableCell>
                <TableCell>Statut</TableCell>
                <TableCell>Priorité</TableCell>
                <TableCell>Service</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {workRequests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    Aucune demande trouvée
                  </TableCell>
                </TableRow>
              ) : (
                workRequests.map((request) => (
                  <TableRow 
                    key={request.id}
                    selected={selectedRequests.includes(request.id)}
                    sx={selectMode && selectedRequests.includes(request.id) ? { backgroundColor: 'rgba(25, 118, 210, 0.08)' } : {}}
                  >
                    {selectMode && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedRequests.includes(request.id)}
                          onChange={() => toggleRequestSelection(request.id)}
                          color="primary"
                        />
                      </TableCell>
                    )}
                    <TableCell>{request.title || request.category || `Demande #${request.id.slice(-7)}`}</TableCell>
                    <TableCell>
                      {format(request.createdAt.toDate(), 'dd/MM/yyyy HH:mm', { locale: fr })}
                    </TableCell>
                    <TableCell>
                      {request.status ? (
                        <Chip
                          label={STATUS_LABELS[request.status] || request.status}
                          color={STATUS_COLORS[request.status] || 'default'}
                          size="small"
                        />
                      ) : null}
                    </TableCell>
                    <TableCell>
                      {request.priority ? (
                        <Chip
                          label={PRIORITY_LABELS[request.priority] || request.priority}
                          color={PRIORITY_COLORS[request.priority] || 'default'}
                          size="small"
                        />
                      ) : null}
                    </TableCell>
                    <TableCell>{request.serviceName}</TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleViewRequest(request.id)}
                        title="Voir les détails"
                      >
                        <VisibilityIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleEditRequest(request.id)}
                        title="Modifier"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteRequest(request.id)}
                        title="Supprimer"
                      >
                        <DeleteIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => generatePDF(request.id)}
                        title="Télécharger en PDF"
                        color="primary"
                      >
                        <PdfIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      {/* Dialog de confirmation de suppression */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirmer la suppression</DialogTitle>
        <DialogContent>
          <Typography>Êtes-vous sûr de vouloir supprimer cette demande ? Cette action est irréversible.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Annuler</Button>
          <Button onClick={confirmDeleteRequest} color="error" variant="contained">
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de détails de la demande */}
      {selectedRequest && (
        <WorkRequestHandler
          request={selectedRequest}
          onClose={() => setSelectedRequest(null)}
          onUpdateSuccess={() => {
            setSelectedRequest(null);
            fetchWorkRequests();
          }}
        />
      )}
    </Container>
  );
}
